import type { BetIdea, JournalEntry, Position, TradeIdea } from '../domain/types'
import type { EdgeLabStore } from './store'
import { todayIso } from '../data/synthetic'
import {
  type ExposureItem,
  type ScoredOpportunity,
  aggregateExposure,
  calibrationReport,
  correlatedExposureWarnings,
  drawdown,
  rankOpportunities,
  riskReward,
} from '../lib/math'
import {
  type DegenerateVerdict,
  type GreedMeter,
  type JournalStats,
  type BehaviorStats,
  degenerateDetector,
  greedMeter,
  journalStats,
} from '../lib/behavior'

type State = EdgeLabStore

function isToday(iso: string): boolean {
  return iso.slice(0, 10) === todayIso()
}

export function bankrollNow(s: State): number {
  const history = s.bankrollHistory
  return history.length ? history[history.length - 1].balance : s.risk.bankroll
}

export function equityCurve(s: State): number[] {
  return s.bankrollHistory.map((b) => b.balance)
}

export function openPositions(s: State): Position[] {
  return s.positions.filter((p) => p.status === 'open')
}

export function openExposureValue(s: State): number {
  return openPositions(s).reduce((acc, p) => acc + Math.abs(p.stake), 0)
}

export function exposureItems(s: State): ExposureItem[] {
  return openPositions(s).map((p) => ({
    id: p.id,
    label: p.label,
    group: p.correlationGroup || 'ungrouped',
    amount: Math.abs(p.stake),
  }))
}

export function realizedPnlToday(s: State): number {
  const fromJournal = s.journal
    .filter((j) => isToday(j.createdAt) && j.realizedPnl !== undefined)
    .reduce((acc, j) => acc + (j.realizedPnl ?? 0), 0)
  const fromPositions = s.positions
    .filter((p) => p.status === 'closed' && p.closedAt && isToday(p.closedAt))
    .reduce((acc, p) => acc + (p.realizedPnl ?? 0), 0)
  return fromJournal + fromPositions
}

export function dailyRiskUsedPct(s: State): number {
  const budget = bankrollNow(s) * s.risk.maxDailyLossPct
  if (budget <= 0) return 0
  const lossToday = Math.min(0, realizedPnlToday(s))
  return Math.min(100, (-lossToday / budget) * 100)
}

export function lossesInRow(entries: JournalEntry[]): number {
  const resolved = entries
    .filter((e) => e.result === 'win' || e.result === 'loss')
    .sort((a, b) => b.createdAt.localeCompare(a.createdAt))
  let streak = 0
  for (const e of resolved) {
    if (e.result === 'loss') streak++
    else break
  }
  return streak
}

export function tradeIdeaToDecimalOdds(idea: TradeIdea): number {
  const rr = riskReward(idea.entry, idea.stop, idea.target)
  return rr.valid ? 1 + rr.ratio : 1.01
}

export function buildOpportunityInputs(s: State) {
  const inputs = []

  for (const idea of s.tradeIdeas) {
    if (idea.status === 'skipped' || idea.status === 'closed') continue
    inputs.push({
      id: idea.id,
      kind: 'stock' as const,
      label: `${idea.symbol} ${idea.direction}`,
      modelProb: idea.modelProb,
      decimalOdds: tradeIdeaToDecimalOdds(idea),
      confidence: idea.confidence,
      dataQuality: idea.dataQuality,
      liquidity: 0.8,
      correlationGroup: idea.correlationGroup,
      notes: idea.thesis,
    })
  }

  for (const bet of s.betIdeas) {
    if (bet.status === 'skipped' || bet.status === 'closed' || bet.modelProb <= 0) continue
    inputs.push({
      id: bet.id,
      kind: bet.kind,
      label: bet.title,
      modelProb: bet.modelProb,
      decimalOdds: bet.decimalOdds,
      confidence: 0.6,
      dataQuality: 0.6,
      liquidity: bet.kind === 'sportsbook' ? 0.8 : 0.6,
      correlationGroup: bet.correlationGroup,
      feePct: bet.feePct,
    })
  }

  for (const market of s.markets) {
    for (const c of market.contracts) {
      if (c.modelProb === undefined || c.settlement !== null) continue
      inputs.push({
        id: c.id,
        kind: 'prediction' as const,
        label: `${market.title} — ${c.title}`,
        modelProb: c.modelProb,
        decimalOdds: 100 / Math.max(1, c.yesPriceCents),
        confidence: 0.6,
        dataQuality: 0.6,
        liquidity: c.liquidityScore,
        correlationGroup: market.correlationGroup,
      })
    }
  }

  for (const o of s.odds) {
    if (o.modelProb === undefined) continue
    inputs.push({
      id: o.id,
      kind: 'sportsbook' as const,
      label: `${o.event} — ${o.selection}`,
      modelProb: o.modelProb,
      decimalOdds: o.decimalOdds,
      confidence: 0.6,
      dataQuality: 0.6,
      liquidity: 0.8,
      correlationGroup: o.correlationGroup,
    })
  }

  return inputs
}

export function opportunities(s: State): ScoredOpportunity[] {
  return rankOpportunities(buildOpportunityInputs(s), s.scanSettings)
}

export interface DashboardSummary {
  bankroll: number
  openPositions: number
  openExposure: number
  openExposurePct: number
  dailyRiskUsedPct: number
  realizedPnlToday: number
  totalPnl: number
  maxDrawdownPct: number
  currentDrawdownPct: number
  winRate: number
  calibrationScore: number
  journal: JournalStats
  greed: GreedMeter
  degenerate: DegenerateVerdict
  bestOpportunities: ScoredOpportunity[]
  warnings: string[]
}

export function dashboardSummary(s: State): DashboardSummary {
  const bankroll = bankrollNow(s)
  const curve = equityCurve(s)
  const dd = drawdown(curve)
  const calib = calibrationReport(
    s.modelPredictions.filter((m) => m.resolved && m.outcome !== undefined).map((m) => ({ prob: m.estProb, outcome: m.outcome as 0 | 1 })),
  )
  const jStats = journalStats(s.journal)
  const exposure = openExposureValue(s)
  const openExposurePct = bankroll > 0 ? (exposure / bankroll) * 100 : 0
  const streak = lossesInRow(s.journal)
  const dailyUsed = dailyRiskUsedPct(s)

  const greed = greedMeter({
    dailyRiskUsedPct: dailyUsed,
    kellyFractionUsed: openExposurePct / 100,
    lossesInRow: streak,
    openExposurePct,
    stakeTrend: 0,
  })

  const todayTrades = s.journal.filter((j) => isToday(j.createdAt) && j.stake > 0).length
  const spanDays = Math.max(1, s.journal.length)
  const degenerate = degenerateDetector({
    tradesToday: todayTrades,
    avgTradesPerDay: s.journal.length / spanDays,
    stakeAfterLossIncreasing: detectStakeChasing(s.journal),
    betsAboveKelly: 0,
    skipsIgnored: 0,
    lossesInRow: streak,
  })

  const exposureWarnings = correlatedExposureWarnings(exposureItems(s), bankroll, {
    maxGroupPct: s.risk.maxCorrelatedPct,
    maxSinglePct: s.risk.maxSinglePositionPct,
  }).map((w) => w.message)

  const warnings = [...exposureWarnings]
  if (dailyUsed >= 100) warnings.push('Daily loss limit reached. Stop trading today.')
  else if (dailyUsed >= 70) warnings.push('Approaching your daily loss limit. Tighten up or stop.')
  if (greed.level === 'tilt') warnings.push('Greed meter in tilt zone — step away from the screen.')
  if (degenerate.degenerate) warnings.push(...degenerate.reasons)
  if (calib.count >= 10 && calib.overconfidenceBias > 0.07) warnings.push('Calibration shows you are overconfident. Shade your probabilities down.')

  return {
    bankroll,
    openPositions: openPositions(s).length,
    openExposure: exposure,
    openExposurePct,
    dailyRiskUsedPct: dailyUsed,
    realizedPnlToday: realizedPnlToday(s),
    totalPnl: curve.length ? curve[curve.length - 1] - curve[0] : 0,
    maxDrawdownPct: dd.maxDrawdownPct * 100,
    currentDrawdownPct: dd.currentDrawdownPct * 100,
    winRate: jStats.winRate,
    calibrationScore: calib.score,
    journal: jStats,
    greed,
    degenerate,
    bestOpportunities: opportunities(s).filter((o) => !o.noTrade.skip).slice(0, 5),
    warnings,
  }
}

function detectStakeChasing(journal: JournalEntry[]): boolean {
  const sorted = [...journal].sort((a, b) => b.createdAt.localeCompare(a.createdAt))
  for (let i = 0; i < sorted.length - 1; i++) {
    if (sorted[i + 1].result === 'loss' && sorted[i].stake > sorted[i + 1].stake && sorted[i].stake > 0) return true
  }
  return false
}

export function exposureBreakdown(s: State) {
  return aggregateExposure(exposureItems(s), bankrollNow(s))
}

export function behaviorStats(s: State): BehaviorStats {
  const j = journalStats(s.journal)
  const last10 = [...s.journal].sort((a, b) => b.createdAt.localeCompare(a.createdAt)).slice(0, 10)
  const tilt = new Set(['fomo', 'revenge', 'tilted'])
  const beatCloseCount = s.betIdeas.filter((b) => b.closingDecimal !== undefined && b.decimalOdds > b.closingDecimal).length
  const killedTheses = [...s.tradeIdeas, ...s.betIdeas].filter((x) => x.status === 'skipped').length
  return {
    totalLogged: s.journal.length,
    skips: j.skips,
    killedTheses,
    beatCloseCount,
    tiltInLast10: last10.some((e) => tilt.has(e.emotionalState)),
    brokeRiskLimitToday: dailyRiskUsedPct(s) >= 100,
  }
}

export function betIdeaStake(bet: BetIdea): number {
  return bet.stake
}
